import React from 'react'
import { useTranslation } from 'react-i18next'
import { useRequestContext } from '@cbgms/components/grid/Contexts/RequestContext'
import {
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemSecondaryAction,
  ListItemText,
  ListSubheader
} from '@mui/material'
import GetAppIcon from '@mui/icons-material/GetApp'
import InsertDriveFileIcon from '@mui/icons-material/InsertDriveFile'
import { IPaginatedRequest } from '@cbgms/api'
import { useBlobDownloader } from '@cbgms/base/hooks/use-blob-downloader'
interface ExportDialogContentProps {
  scope?: string
  gridColumns?: string[]
  loadAction: (params: IPaginatedRequest) => any
}

interface ExportConfig {
  format: 'csv' | 'xlsx'
  columns: 'all' | 'grid'
}

const ExportDialogContent: React.FC<ExportDialogContentProps> = ({ scope, gridColumns, loadAction }) => {
  const { t } = useTranslation()
  const { orderBy, total, search } = useRequestContext()
  const [isLoading, setIsLoading] = React.useState(false)
  const download = useBlobDownloader()

  const fileTypeA = 'CSV'
  const fileTypeB = 'XLSX'

  const handleClick = async (params: ExportConfig) => {
    setIsLoading(true)
    let columns = ''
    if (params.columns === 'all') {
      columns = '*'
    }
    if (params.columns === 'grid') {
      columns = `${gridColumns}`
    }

    try {
      const blob = await loadAction({ format: params.format, orderBy, fields: columns, perPage: total, search })
      download(blob, `${scope}_${params.columns}_columns`)
    } catch (e) {
      console.error(e)
    } finally {
      setIsLoading(false)
    }
  }

  const ExportListItem = ({ format, columns }: ExportConfig) => {
    return (
      <ListItem disableGutters disabled={isLoading} onClick={() => handleClick({ format, columns })}>
        <ListItemIcon>
          <InsertDriveFileIcon />
        </ListItemIcon>
        <ListItemText>
          {t(columns === 'grid' ? 'Grid.ExportWithGridColumns' : 'Grid.ExportWithAllColumns', {
            fileType: format === 'csv' ? fileTypeA : fileTypeB
          })}
        </ListItemText>
        <ListItemSecondaryAction>
          <IconButton color='primary' edge='end' onClick={() => handleClick({ format, columns })} size='large'>
            <GetAppIcon />
          </IconButton>
        </ListItemSecondaryAction>
      </ListItem>
    )
  }

  return (
    <DialogContent
      sx={theme => ({
        padding: theme.spacing(0, 2, 2, 2),
        '& ul li span': {
          color: theme.palette.primary.main,
          cursor: 'pointer',
          '&:hover': {
            textDecoration: 'underline'
          }
        }
      })}
    >
      <List subheader={<ListSubheader disableGutters>{fileTypeA}</ListSubheader>}>
        <ExportListItem format='csv' columns='grid' />
        <ExportListItem format='csv' columns='all' />
      </List>
      <Divider />
      <List subheader={<ListSubheader disableGutters>{fileTypeB}</ListSubheader>}>
        <ExportListItem format='xlsx' columns='grid' />
        <ExportListItem format='xlsx' columns='all' />
      </List>
    </DialogContent>
  )
}

export default ExportDialogContent
